import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

import { ChildListItem, ParentListItem, RootListItem } from './HierarchicalFilterListItems';

const HierarchicalFilter = props => {
  const rootRef = useRef();
  const [lastInteractionType, setLastInteractionType] = useState(null);

  const { rootUrl, rootText, hierarchyInPath, showEmpty } = props.extraProps;

  // create a lookup of slug to option, including a parent traversal
  const remappedBySlug = {};
  const remapBySlug = (option, parent, depth) => {
    remappedBySlug[option.slug] = { ...option, depth, parent };
    if (option.children) {
      option.children.forEach(c => remapBySlug(c, remappedBySlug[option.slug], depth + 1));
    }
  };
  props.options.forEach(c => remapBySlug(c, null, 1));

  const selectedOption = remappedBySlug[props.selected || ''];
  const depth = selectedOption?.depth || 0;
  const leaf = selectedOption;
  const parents = [];
  let visitor = selectedOption;
  while (visitor?.parent) {
    parents.unshift(visitor.parent);
    visitor = visitor.parent;
  }

  const leafHasChildren = leaf && leaf.children && leaf.children.length > 0;
  let children;

  if (leafHasChildren) {
    parents.push(leaf);
    children = leaf.children;
  } else if (parents.length > 0) {
    children = parents[parents.length - 1].children;
  } else {
    children = props.options;
  }

  // Show root links (All applications, etc) if
  // 1. Selected hierarchy is more than one level deep
  // 2. OR top level hierarchy has children
  // 3. Root url and text are provided
  // Ie. show back link if top level application which has children is selected
  // but DO NOT show back link if a top-level application is selected, which does not have any children itself
  const showRoot = (depth > 1 || leafHasChildren) && rootUrl && rootText;

  return (
    <ul className={`filterList hierarch ${props.className}`}>
      {showRoot && (
        <RootListItem
          hierarchyInPath={hierarchyInPath}
          href={hierarchyInPath || rootUrl === '#' ? null : rootUrl}
          onClick={
            hierarchyInPath || rootUrl === '#'
              ? event => {
                  setLastInteractionType(event.type);
                  props.onChange('');
                }
              : null
          }
          ref={rootRef}
          rootText={rootText}
          rootUrl={rootUrl}
          title={props.title}
          toFocusForAccessibility={props.groupHeadingRef}
          selected={props.selected}
        />
      )}
      {parents.map((a, i) => {
        const count = props.filterFunc({ ...props, hierarchical: { ...props.hierarchical, selected: a.slug } }).length;
        return (
          (count > 0 || showEmpty) && (
            <ParentListItem
              hasChildren={Boolean(a.children)}
              key={a.slug}
              lastInteractionType={lastInteractionType}
              name={a.proper_name}
              onClick={event => {
                setLastInteractionType(event.type);
                props.onChange(a.slug);
              }}
              toFocusForAccessibility={rootRef}
              title={props.title}
              selected={props.selected === a.slug}
            />
          )
        );
      })}
      {children
        ? children.map((child, index) => {
            const count = props.filterFunc({ ...props, hierarchical: { ...props.hierarchical, selected: child.slug } })
              .length;

            return (
              (count > 0 || showEmpty) && (
                <ChildListItem
                  key={child.slug}
                  name={child.proper_name}
                  onClick={event => {
                    setLastInteractionType(event.type);
                    props.onChange(child.slug);
                  }}
                  selected={props.selected === child.slug}
                  slug={child.slug}
                  title={props.title}
                />
              )
            );
          })
        : null}
    </ul>
  );
};

HierarchicalFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  baseHierarchy: PropTypes.string,
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  hierarchical: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterFunc: PropTypes.func.isRequired,
  groupHeadingRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default HierarchicalFilter;
